import { useMemo, useRef, useState } from 'react';
import { useClickOutside } from '../../lib/useClickOutside';
import { SLUG_DEFS, type SlugCategory, type SlugDef } from '../../lib/broadcastSlugs';

// Stage 6A §3.4 — slug picker dropdown. Click a slug → onInsert('{token}') at
// the host editor's cursor. Grouped by category with a type-to-filter search.

const CATEGORY_LABEL: Record<SlugCategory, string> = {
  static: 'General',
  contact: 'Recipient',
  account: 'Location / account',
};

const CATEGORY_ORDER: SlugCategory[] = ['static', 'contact', 'account'];

type SlugPickerProps = {
  onInsert: (token: string) => void;
  // The slug catalogue to offer. Defaults to the full broadcast SLUG_DEFS;
  // surveys pass SURVEY_SLUG_DEFS (adds {survey_link}). P3 — additive, so every
  // existing broadcast call site is unchanged.
  defs?: SlugDef[];
};

export function SlugPicker({ onInsert, defs = SLUG_DEFS }: SlugPickerProps) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);
  useClickOutside([wrapRef], () => setOpen(false), open);

  const grouped = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const match = (d: SlugDef) =>
      q === '' ||
      d.token.includes(q) ||
      d.label.toLowerCase().includes(q) ||
      d.description.toLowerCase().includes(q);
    return CATEGORY_ORDER.map((category) => ({
      category,
      defs: defs.filter((d) => d.category === category && match(d)),
    })).filter((g) => g.defs.length > 0);
  }, [filter, defs]);

  const pick = (token: string) => {
    onInsert(`{${token}}`);
    setOpen(false);
    setFilter('');
  };

  return (
    <div ref={wrapRef} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="rounded border border-slate-300 px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
        title="Insert a dynamic slug"
      >
        {'{ }'} Slug
      </button>
      {open && (
        <div className="absolute right-0 z-20 mt-1 w-80 rounded-md border border-slate-200 bg-white shadow-lg">
          <div className="border-b border-slate-100 p-2">
            <input
              autoFocus
              type="search"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter slugs…"
              className="w-full rounded border border-slate-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
            />
          </div>
          <div className="max-h-72 overflow-y-auto p-1">
            {grouped.length === 0 ? (
              <p className="px-2 py-3 text-sm text-slate-400">No slugs match.</p>
            ) : (
              grouped.map((group) => (
                <div key={group.category} className="mb-1">
                  <p className="px-2 py-1 text-xs font-semibold uppercase tracking-wide text-slate-400">
                    {CATEGORY_LABEL[group.category]}
                  </p>
                  {group.defs.map((d) => (
                    <button
                      key={d.token}
                      type="button"
                      onClick={() => pick(d.token)}
                      className="block w-full rounded px-2 py-1 text-left hover:bg-slate-50"
                    >
                      <span className="font-mono text-xs text-slate-900">{`{${d.token}}`}</span>
                      <span className="ml-2 text-xs text-slate-500">{d.label}</span>
                      <span className="block truncate text-xs text-slate-400">
                        {d.description} e.g. {d.example}
                      </span>
                    </button>
                  ))}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
